import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { uploadFlowImage } from '../../../execution';
import Utils from '@utils/utils';
import styles from './index.module.scss';

const LINKED_IMAGE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const FLOW_LINK_PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';

const UploadCard = ({
    label,
    required,
    value,
    linkedLabel,
    linkedEdgeId,
    onRemoveLinked,
    onChange,
    draggable,
    dragging,
    dragOver,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
    onCustomArea,
    customAreaLabel = '重绘区域',
    showUploadSourceActions = false,
    onOpenMaterialLibrary,
    variant = 'default',
    repaintMaskUrl = '',
}) => {
    const dragStartedRef = useRef(false);
    const isLinkedInput = !!linkedEdgeId;
    const isLinkedPlaceholder = value === FLOW_LINK_PLACEHOLDER_ICON;
    const hasResolvedLinkedPreview = isLinkedInput && !!value && !isLinkedPlaceholder;
    const isUpscaleVariant = variant === 'upscale';
    const [blueMaskPreview, setBlueMaskPreview] = useState('');
    const previewValue = (
        value
        && value !== FLOW_LINK_PLACEHOLDER_ICON
        && value !== LINKED_IMAGE_ICON
    ) ? Utils.getImgThumbnail(value) : value;

    useEffect(() => {
        let active = true;

        if (!repaintMaskUrl) {
            setBlueMaskPreview('');
            return () => {
                active = false;
            };
        }

        Utils.convertWhiteImageToBlue(repaintMaskUrl)
            .then((blueMask) => {
                if (active) setBlueMaskPreview(blueMask);
            })
            .catch(() => {
                if (active) setBlueMaskPreview('');
            });

        return () => {
            active = false;
        };
    }, [repaintMaskUrl]);

    const repaintMaskOverlay = blueMaskPreview ? (
        <img src={blueMaskPreview} alt="" className={styles.repaintMaskPreview} draggable={false} />
    ) : null;

    const handleMouseDown = useCallback((event) => {
        event.stopPropagation();
    }, []);

    const handleClick = useCallback((event) => {
        if (dragStartedRef.current) {
            dragStartedRef.current = false;
            event.preventDefault();
        }
    }, []);

    const handleBeforeUpload = useCallback(async (file) => {
        if (!file) return Upload.LIST_IGNORE;
        const fileUrl = await uploadFlowImage(file);
        if (fileUrl) {
            onChange?.(fileUrl);
        }
        return Upload.LIST_IGNORE;
    }, [onChange]);

    const handleDragStart = useCallback((event) => {
        if (!draggable) return;
        dragStartedRef.current = true;
        event.dataTransfer.effectAllowed = 'move';
        onDragStart?.(event);
    }, [draggable, onDragStart]);

    const handleDragEnd = useCallback((event) => {
        window.setTimeout(() => {
            dragStartedRef.current = false;
        }, 0);
        onDragEnd?.(event);
    }, [onDragEnd]);

    const handleFileDragOver = useCallback((event) => {
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (hasFile) {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = 'copy';
            return;
        }

        onDragOver?.(event);
    }, [onDragOver]);

    const handleFileDrop = useCallback(async (event) => {
        const files = Array.from(event.dataTransfer?.files || []);
        const droppedFile = files[0];

        if (!droppedFile) {
            onDrop?.(event);
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (linkedEdgeId) {
            message.warning('请先解除连线再上传图片');
            return;
        }
        const fileUrl = await uploadFlowImage(droppedFile);
        if (fileUrl) {
            onChange?.(fileUrl);
        }
    }, [linkedEdgeId, onChange, onDrop]);

    const handleRemove = useCallback((event) => {
        event.stopPropagation();
        if (linkedEdgeId && typeof onRemoveLinked === 'function') {
            onRemoveLinked();
            return;
        }
        onChange?.(null);
    }, [linkedEdgeId, onChange, onRemoveLinked]);

    const handleCustomArea = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onCustomArea?.();
    }, [onCustomArea]);

    const handleOpenMaterialLibrary = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenMaterialLibrary?.();
    }, [onOpenMaterialLibrary]);

    const renderFilledActions = () => (
        onCustomArea ? (
            <div className={styles.filledActions}>
                <button
                    type="button"
                    className={styles.customAreaButton}
                    onClick={handleCustomArea}
                    draggable={false}
                >
                    {customAreaLabel}
                </button>
                <button
                    type="button"
                    className={styles.actionRemoveButton}
                    onClick={handleRemove}
                    draggable={false}
                    aria-label={`删除${label}`}
                >
                    删除图片
                </button>
            </div>
        ) : (
            <button
                type="button"
                className={styles.removeButton}
                onClick={handleRemove}
                draggable={false}
                aria-label={`删除${label}`}
            >
                <DeleteOutlined className={styles.removeIcon} />
            </button>
        )
    );

    return (
        <div className={`${styles.wrapper} ${isUpscaleVariant ? styles.wrapperUpscale : ''}`}>
            {!isUpscaleVariant ? (
                <span className={`${styles.label} ${required ? styles.required : ''}`}>
                    {required ? <span className={styles.asterisk}>*</span> : null}
                    {label}
                </span>
            ) : null}
            <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleBeforeUpload}
                openFileDialogOnClick={!dragging}
            >
                <button
                    type="button"
                    className={`${styles.card} ${isUpscaleVariant ? styles.cardUpscale : ''} ${draggable ? styles.cardDraggable : ''} ${dragging ? styles.cardDragging : ''} ${dragOver ? styles.cardDragOver : ''}`}
                    onMouseDown={handleMouseDown}
                    onClick={handleClick}
                    draggable={draggable}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleFileDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={handleFileDrop}
                >
                    {value ? (
                        isLinkedPlaceholder ? (
                            <>
                                <div className={styles.linkedPlaceholder}>
                                    <img src={previewValue} alt="" width={24} height={24} className={styles.linkedPlaceholderIcon} draggable={false} />
                                    {linkedLabel ? <span className={styles.linkedPlaceholderLabel}>{linkedLabel}</span> : null}
                                </div>
                                <button
                                    type="button"
                                    className={`${styles.removeButton} ${styles.removeButtonPlaceholder}`}
                                    onClick={handleRemove}
                                    draggable={false}
                                    aria-label={`删除${label}`}
                                >
                                    <DeleteOutlined className={styles.removeIcon} />
                                </button>
                            </>
                        ) : hasResolvedLinkedPreview ? (
                            <>
                                <img src={previewValue} alt={label} className={styles.preview} draggable={false} />
                                {repaintMaskOverlay}
                                {isUpscaleVariant ? <div className={styles.previewTitle}>{label}</div> : null}
                                {renderFilledActions()}
                            </>
                        ) : isLinkedInput ? (
                            <>
                                <div className={styles.linkedPlaceholder}>
                                    <img src={LINKED_IMAGE_ICON} alt="" width={24} height={24} className={styles.linkedPlaceholderIcon} draggable={false} />
                                    {linkedLabel ? <span className={styles.linkedPlaceholderLabel}>{linkedLabel}</span> : null}
                                </div>
                                <button
                                    type="button"
                                    className={`${styles.removeButton} ${styles.removeButtonPlaceholder}`}
                                    onClick={handleRemove}
                                    draggable={false}
                                    aria-label={`删除${label}`}
                                >
                                    <DeleteOutlined className={styles.removeIcon} />
                                </button>
                            </>
                        ) : (
                            <>
                                <img src={previewValue} alt={label} className={styles.preview} draggable={false} />
                                {repaintMaskOverlay}
                                {isUpscaleVariant ? <div className={styles.previewTitle}>{label}</div> : null}
                                {renderFilledActions()}
                            </>
                        )
                    ) : linkedEdgeId ? (
                        <>
                            <div className={styles.linkedPlaceholder}>
                                <img src={LINKED_IMAGE_ICON} alt="" width={24} height={24} draggable={false} />
                                <span className={styles.linkedPlaceholderLabel}>{linkedLabel}</span>
                            </div>
                            <button
                                type="button"
                                className={`${styles.removeButton} ${styles.removeButtonPlaceholder}`}
                                onClick={handleRemove}
                                draggable={false}
                                aria-label={`删除${label}`}
                            >
                                <DeleteOutlined className={styles.removeIcon} />
                            </button>
                        </>
                    ) : (
                        isUpscaleVariant ? (
                            <div className={styles.emptyState}>
                                <div className={styles.plusIcon} />
                                <span className={styles.placeholderText}>{label}</span>
                            </div>
                        ) : <div className={styles.plusIcon} />
                    )}
                </button>
            </Upload>
            {showUploadSourceActions && !value && !linkedEdgeId ? (
                <div className={styles.emptyActions}>
                    <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={handleBeforeUpload}
                    >
                        <button
                            type="button"
                            className={styles.emptyActionButton}
                            onMouseDown={handleMouseDown}
                        >
                            本地上传
                        </button>
                    </Upload>
                    {onOpenMaterialLibrary ? (
                        <button
                            type="button"
                            className={styles.emptyActionButton}
                            onMouseDown={handleMouseDown}
                            onClick={handleOpenMaterialLibrary}
                        >
                            素材库
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default UploadCard;
